// src/components/popups/DeleteCommentDrawer.js
import React, { useEffect, useState } from 'react';
import './DeleteCommentDrawer.css';

export default function DeleteCommentDrawer({
  isOpen,
  onClose,
  onConfirm,             // async () => void      // must perform the actual API delete
  onOptimistic,          // () => void            // 🆕 prune immediately in CommentDrawer (optional)
  onRollback,            // () => void            // 🆕 restore if API fails (optional)
  type = 'comment',      // 'comment' | 'reply'
  authorUsername = '',
  textSnippet = '',
  createdAt = '',
}) {
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    const onKey = (e) => {
      if (!isOpen) return;
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  useEffect(() => {
    // reset state when opened/closed
    if (!isOpen) {
      setSubmitting(false);
      setErr('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const noun = type === 'reply' ? 'reply' : 'comment';
  const title = `Delete ${noun}?`;
  const confirmText = `Delete ${noun}`;

  const shortSnippet =
    textSnippet?.length > 120 ? textSnippet.slice(0, 117) + '…' : textSnippet;

  const handleDelete = async () => {
    // 1) Immediately update UI optimistically
    try {
      setSubmitting(true);
      setErr('');
      onOptimistic?.();        // 🔥 tell CommentDrawer to remove it right now

      // 2) Perform API delete
      await onConfirm?.();

      // 3) Close after success
      onClose?.();
    } catch (e) {
      // API failed — roll back the optimistic removal if provided
      onRollback?.();
      setErr(e?.message || 'Failed to delete. Try again.');
      setSubmitting(false);
    }
  };

  return (
    <div className="delc-backdrop" onClick={onClose}>
      <div
        className="delc-card"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="delc-title"
      >
        <div className="delc-grabber" />

        <h3 id="delc-title" className="delc-title">{title}</h3>

        {(authorUsername || shortSnippet || createdAt) && (
          <div className="delc-preview">
            {authorUsername && (
              <div className="delc-row">
                <span className="delc-label">From</span>
                <span className="delc-value">@{authorUsername}</span>
              </div>
            )}
            {createdAt && (
              <div className="delc-row">
                <span className="delc-label">Time</span>
                <span className="delc-value">{createdAt}</span>
              </div>
            )}
            {shortSnippet && (
              <div className="delc-snippet">“{shortSnippet}”</div>
            )}
          </div>
        )}

        <p className="delc-warning">
          This {noun} will be removed permanently for everyone. You can’t undo this.
        </p>

        {err && <div className="delc-error">{err}</div>}

        <div className="delc-actions">
          <button
            type="button"
            className="delc-btn delc-cancel"
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="delc-btn delc-delete"
            onClick={handleDelete}
            disabled={submitting}
            aria-busy={submitting ? 'true' : 'false'}
          >
            {submitting ? 'Deleting…' : confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}

